import React, { useState, useEffect, useRef, useCallback } from "react";
import { getCookie, setCookie } from "helpers";
import { accountSwitcherEnabled } from "api/constants";

/**
 * Loads dev users from public/ONLY_DEV_USERS.json
 * Falls back to ONLY_DEV_USERS in localStorage if the file is empty or unavailable.
 * Format: { [userId]: { bearer: string, userInfo: string } }
 */
async function loadDevUsers() {
  // 1. Try fetching the JSON file first
  try {
    const res = await fetch("/ONLY_DEV_USERS.json", { cache: "no-store" });
    if (res.ok) {
      const data = await res.json();
      if (data && typeof data === "object" && Object.keys(data).length > 0) {
        return data;
      }
    }
  } catch {
    /* fall through to localStorage fallback */
  }

  // 2. Fallback: ONLY_DEV_USERS in localStorage
  try {
    const raw = localStorage.getItem("ONLY_DEV_USERS");
    if (raw) {
      try {
        return JSON.parse(raw);
      } catch {
        return JSON.parse(decodeURIComponent(raw));
      }
    }
  } catch {
    /* ignore */
  }

  return {};
}

/**
 * Returns the initial letter(s) of a full name for the avatar.
 */
function getInitials(fullName) {
  if (!fullName) return "?";
  const parts = fullName.trim().split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * A small colour palette to assign avatar colours deterministically by userId.
 */
const AVATAR_COLORS = [
  "#7c3aed",
  "#3b82f6",
  "#059669",
  "#d97706",
  "#db2777",
  "#0891b2",
];

function getAvatarColor(userId) {
  const idx = (parseInt(userId, 10) || 0) % AVATAR_COLORS.length;
  return AVATAR_COLORS[idx];
}

export default function AccountSwitcher() {
  const [isOpen, setIsOpen] = useState(false);
  const [users, setUsers] = useState({});
  const [activeUserId, setActiveUserId] = useState(null);
  const wrapperRef = useRef(null);

  // Re-load data every time the dropdown is opened so the list is fresh.
  const refreshState = useCallback(async () => {
    const devUsers = await loadDevUsers();
    setUsers(devUsers);

    const currentBearer = getCookie("Bearer");
    // Find which userId has the matching bearer
    const matchedId = Object.entries(devUsers).find(
      ([, data]) => data.bearer === currentBearer,
    )?.[0];
    setActiveUserId(matchedId || null);
  }, []);

  useEffect(() => {
    refreshState();
  }, [refreshState]);

  // Seed the current session into ONLY_DEV_USERS in localStorage (dev-only)
  useEffect(() => {
    if (!accountSwitcherEnabled) return;
    try {
      const bearer = getCookie("Bearer");
      const userInfoRaw = getCookie("userInfo");
      if (bearer && userInfoRaw) {
        const userInfoObj = JSON.parse(decodeURIComponent(userInfoRaw));
        const userId = userInfoObj?.userId;
        if (userId) {
          let devUsers = {};
          try {
            const existing = localStorage.getItem("ONLY_DEV_USERS");
            if (existing) {
              try {
                devUsers = JSON.parse(existing);
              } catch {
                devUsers = JSON.parse(decodeURIComponent(existing));
              }
            }
          } catch {
            /* ignore */
          }
          devUsers[userId] = { bearer, userInfo: userInfoRaw };
          localStorage.setItem("ONLY_DEV_USERS", JSON.stringify(devUsers));
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Don't render in production
  if (!accountSwitcherEnabled) return null;

  // Don't render if there are no saved accounts yet
  const userEntries = Object.entries(users);
  if (userEntries.length === 0) return null;

  const activeUser = activeUserId ? users[activeUserId] : null;
  let activeUserInfo = null;
  try {
    activeUserInfo = activeUser
      ? JSON.parse(decodeURIComponent(activeUser.userInfo))
      : null;
  } catch {
    activeUserInfo = null;
  }

  const activeLabel = activeUserInfo?.fullName?.split(" ").pop() || "Dev";
  const activeRole = activeUserInfo?.roleCode || "";
  const activeColor = getAvatarColor(activeUserId);
  const activeInitials = getInitials(activeUserInfo?.fullName);

  const handleToggle = (e) => {
    e.stopPropagation();
    refreshState();
    setIsOpen((prev) => !prev);
  };

  const handleSwitch = (userId) => {
    if (userId === activeUserId) {
      setIsOpen(false);
      return;
    }
    const target = users[userId];
    if (!target) return;

    // Clear existing auth cookies
    document.cookie =
      "userInfo=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";
    document.cookie = "Bearer=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/";

    // Set new auth cookies (30 days)
    setCookie("Bearer", target.bearer, 30);
    setCookie("userInfo", target.userInfo, 30);

    window.location.reload();
  };

  const handleCopyJson = async (e) => {
    e.stopPropagation();
    try {
      // Read from localStorage fallback to get the latest data
      const raw = localStorage.getItem("ONLY_DEV_USERS");
      let localUsers = {};
      if (raw) {
        try {
          localUsers = JSON.parse(raw);
        } catch {
          localUsers = JSON.parse(decodeURIComponent(raw));
        }
      }
      // Merge with currently loaded users (from file)
      const merged = { ...users, ...localUsers };
      const text = JSON.stringify(merged, null, 2);
      await navigator.clipboard.writeText(text);
      alert("✅ Copied!\n\nPaste into:\n.public/ONLY_DEV_USERS.json");
    } catch {
      alert("❌ Could not copy to clipboard.");
    }
  };

  return (
    <div ref={wrapperRef} style={styles.wrapper}>
      {/* Trigger button */}
      <button
        style={styles.btn}
        onClick={handleToggle}
        title="Switch dev account"
      >
        <span style={{ ...styles.avatar, backgroundColor: activeColor }}>
          {activeInitials}
        </span>
        <span style={styles.label}>{activeLabel}</span>
        <span style={styles.tag}>{activeRole}</span>
        <span style={styles.chevron}>▾</span>
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div style={styles.dropdown}>
          <div style={styles.dropdownHeader}>Dev Accounts</div>
          {userEntries.map(([userId, data]) => {
            let info = null;
            try {
              info = JSON.parse(decodeURIComponent(data.userInfo));
            } catch {
              info = null;
            }
            const isActive = userId === activeUserId;
            const color = getAvatarColor(userId);
            const initials = getInitials(info?.fullName);
            const name = info?.fullName || userId;
            const role = info?.roleCode || "";

            return (
              <div
                key={userId}
                style={{
                  ...styles.item,
                  ...(isActive ? styles.itemActive : {}),
                }}
                onClick={() => handleSwitch(userId)}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "#334155";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = isActive
                    ? "#0f172a"
                    : "transparent";
                }}
              >
                <span style={{ ...styles.itemAvatar, backgroundColor: color }}>
                  {initials}
                </span>
                <div style={styles.itemInfo}>
                  <div style={styles.itemName}>{name}</div>
                  <div style={styles.itemRole}>{role}</div>
                </div>
                {isActive && <span style={styles.checkmark}>✓</span>}
              </div>
            );
          })}
          {/* Footer: only on localhost – copy current cookie data to clipboard for pasting into JSON file */}
          <div style={styles.copyFooter}>
            <button
              style={styles.copyBtn}
              onClick={handleCopyJson}
              title="Copy current session data to paste into public/ONLY_DEV_USERS.json"
            >
              📋 Copy JSON to file
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Inline styles (keeps the component fully self-contained)
// ---------------------------------------------------------------------------
const styles = {
  wrapper: {
    position: "relative",
    marginRight: "10px",
    fontFamily: "'Nunito', 'Inter', sans-serif",
    fontSize: "12px",
    userSelect: "none",
  },
  btn: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    background: "#1e293b",
    color: "#e2e8f0",
    border: "1px solid #334155",
    borderRadius: "20px",
    padding: "4px 10px 4px 6px",
    cursor: "pointer",
    boxShadow: "0 2px 8px rgba(0,0,0,0.35)",
    transition: "background 0.15s, box-shadow 0.15s",
    whiteSpace: "nowrap",
    outline: "none",
  },
  avatar: {
    width: "22px",
    height: "22px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: 800,
    color: "#fff",
    flexShrink: 0,
  },
  label: {
    fontWeight: 700,
    letterSpacing: "0.01em",
  },
  tag: {
    fontSize: "10px",
    fontWeight: 600,
    background: "#0f172a",
    borderRadius: "8px",
    padding: "1px 5px",
    color: "#94a3b8",
    marginLeft: "2px",
  },
  chevron: {
    marginLeft: "2px",
    fontSize: "10px",
    color: "#64748b",
  },
  dropdown: {
    position: "absolute",
    top: "calc(100% + 6px)",
    right: 0,
    background: "#1e293b",
    border: "1px solid #334155",
    borderRadius: "10px",
    overflow: "hidden",
    boxShadow: "0 6px 20px rgba(0,0,0,0.4)",
    minWidth: "180px",
    zIndex: 99999,
  },
  dropdownHeader: {
    padding: "8px 12px 6px",
    fontSize: "10px",
    fontWeight: 700,
    color: "#475569",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    borderBottom: "1px solid #334155",
  },
  item: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 12px",
    cursor: "pointer",
    color: "#cbd5e1",
    transition: "background 0.12s",
    background: "transparent",
  },
  itemActive: {
    background: "#0f172a",
    color: "#fff",
  },
  itemAvatar: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "10px",
    fontWeight: 800,
    color: "#fff",
    flexShrink: 0,
  },
  itemInfo: {
    flex: 1,
  },
  itemName: {
    fontWeight: 700,
    fontSize: "12px",
  },
  itemRole: {
    fontSize: "10px",
    color: "#64748b",
  },
  checkmark: {
    color: "#22c55e",
    fontSize: "12px",
    fontWeight: 700,
  },
  copyFooter: {
    borderTop: "1px solid #334155",
    padding: "6px 8px",
  },
  copyBtn: {
    width: "100%",
    background: "none",
    border: "1px solid #334155",
    borderRadius: "6px",
    color: "#64748b",
    fontSize: "10px",
    fontWeight: 600,
    padding: "4px 8px",
    cursor: "pointer",
    textAlign: "left",
    transition: "background 0.12s, color 0.12s",
  },
};
